import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  nextUntaggedSendGate,
  UNTAGGED_COMMENT_WARNING,
  type UntaggedSendSource,
} from '../../utils/untaggedCommentConfirm';
import './UntaggedSendHint.css';

/** Tracks the "click send again" confirm. `draftKey` clears it when the comment changes. */
export function useUntaggedSendGate(draftKey: string) {
  const awaitingRef = useRef(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    awaitingRef.current = false;
    setOpen(false);
  }, [draftKey]);

  const allow = useCallback((tagged: boolean, source: UntaggedSendSource, skip = false) => {
    const next = nextUntaggedSendGate({
      tagged,
      source,
      awaitingClick: awaitingRef.current,
      skip,
    });
    awaitingRef.current = next.awaitingClick;
    setOpen(next.awaitingClick);
    return next.action === 'send';
  }, []);

  const dismiss = useCallback(() => {
    awaitingRef.current = false;
    setOpen(false);
  }, []);

  return { open, allow, dismiss };
}

/** Warning anchored above a send control. The button stays clickable underneath. */
const UntaggedSendHint: React.FC<{
  open: boolean;
  onDismiss: () => void;
  children: React.ReactNode;
}> = ({ open, onDismiss, children }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    const place = () => {
      const node = ref.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      setAnchor({ top: rect.top, right: rect.right });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const node = ref.current;
      if (!node || !(event.target instanceof Node) || node.contains(event.target)) return;
      onDismiss();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onDismiss]);

  return (
    <span className="untagged-send-anchor" ref={ref}>
      {open && anchor
        ? createPortal(
            <span
              className="untagged-send-hint"
              role="status"
              style={{ top: anchor.top - 8, left: anchor.right, transform: 'translate(-100%, -100%)' }}
            >
              {UNTAGGED_COMMENT_WARNING}
            </span>,
            document.body,
          )
        : null}
      {children}
    </span>
  );
};

export default UntaggedSendHint;
